import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation, NavLink, Outlet, Routes, Route, Navigate } from "react-router-dom";
import { api, formatApiError, wsToken, agencyToken, workspaceLogoUrl } from "@/lib/api";
import { WorkspaceProvider } from "@/context/WorkspaceContext";
import AuthScreen from "@/components/AuthScreen";
import { NotificationBell } from "@/components/NotificationBell";
import { ProfileDialog } from "@/components/ProfileDialog";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher";
import { Onboarding } from "@/components/Onboarding";
import { WelcomeVideoDialog } from "@/components/WelcomeVideo";
import { Avatar } from "@/components/shared";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { LayoutDashboard, MessagesSquare, Table2, FolderKanban, Ticket, FolderOpen, Settings, LogOut, ChevronDown, ArrowLeft, Sparkles, KanbanSquare, Bot, User as UserIcon, Candy, LayoutGrid, BookOpen, Crown, FolderLock, FileText, Inbox as InboxIcon, PanelLeftClose, PanelLeftOpen, PlayCircle } from "lucide-react";
import Dashboard from "@/pages/Dashboard";
import Comms from "@/pages/Comms";
import ClientDocView from "@/pages/ClientDocView";
import { ClientVideoView } from "@/pages/ClientVideoView";
import ClientDocs from "@/pages/ClientDocs";
import ClientSocial from "@/pages/ClientSocial";
import { Share2 } from "lucide-react";
import Leads from "@/pages/Leads";
import Reports from "@/pages/Reports";
import ChannelReports from "@/pages/ChannelReports";
import ReportView from "@/pages/ReportView";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import Files from "@/pages/Files";
import MDaaS from "@/pages/MDaaS";
import Topo from "@/pages/Topo";
import SnackWall from "@/pages/SnackWall";
import KnowledgeBase from "@/pages/KnowledgeBase";
import ClientForms from "@/pages/ClientForms";
import CMOaaS from "@/pages/CMOaaS";
import Apps from "@/pages/Apps";
import ConsoleInternal from "@/pages/ConsoleInternal";
import ClientInbox from "@/pages/ClientInbox";
import { CommsBubbles } from "@/components/CommsBubbles";

const MODULE_NAV = {
  inbox: { label: "Inbox", icon: InboxIcon, path: "inbox" },
  comms: { label: "Comms", icon: MessagesSquare, path: "comms" },
  leads: { label: "Leads", icon: Table2, path: "leads" },
  docs: { label: "Docs", icon: FileText, path: "docs" },
  social: { label: "Social", icon: Share2, path: "social" },
  reports: { label: "Reports", icon: FolderKanban, path: "reports" },
  tickets: { label: "Tickets", icon: Ticket, path: "tickets" },
  files: { label: "Files", icon: FolderOpen, path: "files" },
  mdaas: { label: "MDaaS", icon: KanbanSquare, path: "mdaas" },
  internal: { label: "Internal", icon: FolderLock, path: "internal" },
};
const WS_ROLE_LABEL = { super_admin: "Super Admin", manager: "Manager", crew: "Crew", client_admin: "Client Admin", client_manager: "Client Manager", client_crew: "Client Crew" };

function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [660, 880].forEach((f, idx) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f;
      const t = now + idx * 0.14;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.1, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
      o.connect(g); g.connect(ctx.destination);
      o.start(t); o.stop(t + 0.55);
    });
    setTimeout(() => ctx.close().catch(() => {}), 1300);
  } catch { /* ignore */ }
}

function useTopoActivity(enabled) {
  const [working, setWorking] = useState(false);
  const baseline = useRef(undefined); // last_done_at seen; undefined until first poll
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/topo/activity");
        if (!alive) return;
        setWorking(!!data.working);
        const done = data.last_done_at || null;
        if (baseline.current === undefined) baseline.current = done;
        else if (done && done !== baseline.current) { baseline.current = done; playChime(); }
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(poll, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [enabled]);
  return working;
}

function ConsoleSettingsRedirect({ slug }) {
  useEffect(() => { window.location.replace(`/console/clients/${slug}`); }, [slug]);
  return null;
}

export default function WorkspaceApp() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [pub, setPub] = useState(null);

  const load = async () => {
    if (!wsToken()) { setSession(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      if (data.context !== "workspace" || data.workspace?.slug !== slug) { setSession(false); return; }
      setSession(data);
    } catch { setSession(false); }
  };
  useEffect(() => { load(); api.get(`/workspace-public/${slug}`).then((r) => setPub(r.data)).catch(() => setPub({ notfound: true })); /* eslint-disable-next-line */ }, [slug]);

  const onLogin = (email, password, deviceToken) => api.post(`/auth/workspace/${slug}/login`, { email, password, device_token: deviceToken }).then((r) => r.data);
  const onAuthenticated = async (data) => { localStorage.setItem("mc_ws_token", data.token); await load(); };

  if (session === null) return <div className="min-h-screen flex items-center justify-center bg-white text-[#9FA1A4] text-sm">Loading…</div>;
  if (pub?.notfound) return <div className="min-h-screen flex items-center justify-center bg-white text-[#595D62] text-sm">Workspace not found.</div>;
  if (session === false) {
    return <AuthScreen brandName={pub?.name || slug} logoUrl={pub?.logo_url} deviceScope={`ws:${slug}`} eyebrow="Client workspace" title={`Sign in to ${pub?.name || slug}`} heroTitle={`Hi ${pub?.name || slug} 👋`} heroSubtitle="Sign into your Mission Control." onLogin={onLogin} onAuthenticated={onAuthenticated} />;
  }

  const logout = () => { localStorage.removeItem("mc_ws_token"); setSession(false); };
  const ctx = { ...session, slug, canEdit: session.can_edit, canManageSettings: session.can_manage_settings, canManageVisibility: session.can_manage_visibility, visibleModules: (session.visible_modules || []).filter((m) => m !== "files"), logout, reload: load };

  return (
    <WorkspaceProvider value={ctx}>
      <Routes>
        <Route path="snackwall" element={<SnackWall />} />
        <Route path="docs/:docId" element={<ClientDocView />} />
        <Route path="videos/:videoId" element={<ClientVideoView />} />
        <Route element={<Layout ctx={ctx} navigate={navigate} />}>
          <Route index element={<Dashboard />} />
          {ctx.visibleModules.includes("inbox") && <Route path="inbox" element={<ClientInbox />} />}
          {ctx.visibleModules.includes("comms") && <Route path="comms" element={<Comms />} />}
          {ctx.visibleModules.includes("leads") && <Route path="leads" element={<Leads />} />}
          {ctx.visibleModules.includes("docs") && <Route path="docs" element={<ClientDocs />} />}
          <Route path="social" element={<ClientSocial />} />
          <Route path="social/:groupId" element={<ClientSocial />} />
          {ctx.visibleModules.includes("reports") && <Route path="reports" element={<Reports />} />}
          {ctx.visibleModules.includes("reports") && <Route path="reports/:channelId" element={<ChannelReports />} />}
          {ctx.visibleModules.includes("reports") && <Route path="reports/:channelId/:reportId" element={<ReportView />} />}
          {ctx.visibleModules.includes("tickets") && <Route path="tickets" element={<Tickets />} />}
          {ctx.visibleModules.includes("tickets") && <Route path="tickets/:ticketId" element={<TicketDetail />} />}
          {ctx.visibleModules.includes("files") && <Route path="files" element={<Files />} />}
          {ctx.visibleModules.includes("mdaas") && <Route path="mdaas" element={<MDaaS />} />}
          {ctx.visibleModules.includes("internal") && <Route path="internal" element={<ConsoleInternal scopeClientId={ctx.workspace.id} />} />}
          {ctx.canManageSettings && <Route path="settings" element={<ConsoleSettingsRedirect slug={slug} />} />}
          <Route path="knowledge/*" element={<KnowledgeBase />} />
          <Route path="forms" element={<ClientForms />} />
          <Route path="forms/:formId" element={<ClientForms />} />
          <Route path="apps" element={<Apps />} />
          {ctx.visibleModules.includes("cmoaas") && <Route path="cmoaas" element={<CMOaaS />} />}
          <Route path="*" element={<Navigate to={`/${slug}`} replace />} />
        </Route>
      </Routes>
    </WorkspaceProvider>
  );
}

function Layout({ ctx, navigate }) {
  const { workspace, user, slug, visibleModules, canManageSettings } = ctx;
  const [profileOpen, setProfileOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  useEffect(() => { document.body.dataset.escClose = "1"; return () => { delete document.body.dataset.escClose; }; }, []);
  const [videoDismissed, setVideoDismissed] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("mc_ws_sidebar_collapsed") !== "0");
  const toggleCollapsed = () => setCollapsed((c) => { const n = !c; localStorage.setItem("mc_ws_sidebar_collapsed", n ? "1" : "0"); return n; });
  const base = `/${slug}`;
  const logo = workspaceLogoUrl(workspace);
  const navItems = visibleModules.map((m) => MODULE_NAV[m]).filter(Boolean);
  const topoWorking = useTopoActivity(visibleModules.includes("topo"));
  const { pathname } = useLocation();
  const isActivePath = (to, end) => (end ? pathname === to : pathname === to || pathname.startsWith(to + "/"));
  const NavRow = ({ to, end, testid, icon: Icon, label, trailing }) => {
    const active = isActivePath(to, end);
    const cls = `mc-nav-item w-full flex items-center rounded-2xl py-2.5 text-sm ${collapsed ? "justify-center px-0" : "gap-3 px-3.5"} ${active ? "mc-nav-active font-semibold text-white" : "text-[#9FA1A4]"}`;
    const link = (
      <NavLink to={to} end={end} data-testid={testid} className={cls}>
        <Icon className="h-[18px] w-[18px] shrink-0" />{!collapsed && <>{label}{trailing}</>}
      </NavLink>
    );
    if (!collapsed) return link;
    return <Tooltip><TooltipTrigger asChild>{link}</TooltipTrigger><TooltipContent side="right">{label}</TooltipContent></Tooltip>;
  };

  const navEntries = [];
  navEntries.push({ key: "dashboard", el: (
    <NavRow to={base} end testid="sidebar-nav-dashboard" icon={LayoutDashboard} label="Dashboard" />
  ) });
  navItems.forEach((it) => navEntries.push({ key: it.path, el: (
    <NavRow to={`${base}/${it.path}`} testid={`sidebar-nav-${it.path}`} icon={it.icon} label={it.label}
      trailing={it.path === "topo" && topoWorking ? (
        <span data-testid="topo-sidebar-working" title="TOPO is working on a deliverable…" className="ml-auto flex items-center gap-1.5 text-[10px] font-medium text-[#5b2fd6]">
          <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#723FFF] opacity-60" /><span className="relative inline-flex rounded-full h-2 w-2 bg-[#723FFF]" /></span>
          working
        </span>
      ) : null} />
  ) }));
  navEntries.push({ key: "knowledge", el: (
    <NavRow to={`${base}/knowledge`} testid="sidebar-nav-knowledge" icon={BookOpen} label="Knowledge Base" />
  ) });
  navEntries.push({ key: "forms", el: (
    <NavRow to={`${base}/forms`} testid="sidebar-nav-forms" icon={FileText} label="Forms" />
  ) });
  navEntries.push({ key: "apps", el: (
    <NavRow to={`${base}/apps`} testid="sidebar-nav-apps" icon={LayoutGrid} label="Apps" />
  ) });
  if (visibleModules.includes("cmoaas")) navEntries.push({ key: "cmoaas", el: (
    <NavRow to={`${base}/cmoaas`} testid="sidebar-nav-cmoaas" icon={Crown} label="CMOaaS" />
  ) });
  navEntries.push({ key: "snackwall", el: (
    <NavRow to={`${base}/snackwall`} testid="sidebar-nav-snackwall" icon={Candy} label="SnackWall"
      trailing={<span className="ml-auto font-mono-data text-[9px] uppercase tracking-wider text-[#b7791f] bg-[#F59E0B]/15 rounded-full px-1.5 py-0.5">Fun</span>} />
  ) });
  const navOrder = ctx.nav_order || [];
  const navIdx = (k) => { const i = navOrder.indexOf(k); return i === -1 ? 999 : i; };
  navEntries.sort((a, b) => navIdx(a.key) - navIdx(b.key));

  return (
    <div className="min-h-screen bg-white flex">
      <TooltipProvider delayDuration={100}>
      <aside style={{ "--mc-accent": workspace.accent_color || "#723FFF" }} className={`${collapsed ? "w-16" : "w-64"} shrink-0 border-r border-white/[0.06] bg-[#0B0B12] flex flex-col fixed h-screen transition-[width] duration-200`}>
        <div className={collapsed ? "p-3" : "p-5"}>
          {collapsed ? (
            <div className="flex items-center justify-center" data-testid="workspace-identity">
              {logo ? <img src={logo} alt="" className="h-9 w-9 rounded-xl object-cover bg-white" data-testid="workspace-logo" /> : <img src="/shuttle-logo.png" alt="" className="h-9 w-9 object-contain invert" data-testid="workspace-logo" />}
            </div>
          ) : (
            <div className="relative flex items-center gap-3 rounded-2xl bg-white/[0.04] border border-white/[0.06] p-3 pr-9" data-testid="workspace-identity">
              {logo ? <img src={logo} alt="" className="h-9 w-9 rounded-xl object-cover bg-white" data-testid="workspace-logo" /> : <img src="/shuttle-logo.png" alt="" className="h-9 w-9 object-contain invert" data-testid="workspace-logo" />}
              <div className="min-w-0"><p className="font-display text-sm font-semibold text-white truncate">{workspace.name}</p><p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">{WS_ROLE_LABEL[user.ws_role]}</p></div>
              <button data-testid="workspace-switcher-trigger" onClick={() => setSwitcherOpen(true)} title="Switch workspace" className="absolute top-2 right-2 h-6 w-6 rounded-lg flex items-center justify-center text-[#9FA1A4] hover:text-white hover:bg-white/10 transition-colors"><ChevronDown className="h-4 w-4" /></button>
            </div>
          )}
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {navEntries.map((e) => <React.Fragment key={e.key}>{e.el}</React.Fragment>)}
        </nav>
        <div className="p-3 border-t border-white/[0.06]">
          <button data-testid="workspace-sidebar-collapse" onClick={toggleCollapsed}
            className={`w-full flex items-center rounded-2xl py-2.5 text-sm text-[#7c7e85] hover:text-white hover:bg-white/[0.06] transition-all ${collapsed ? "justify-center px-0" : "gap-3 px-3.5"}`}>
            {collapsed ? <PanelLeftOpen className="h-[18px] w-[18px] shrink-0" /> : <><PanelLeftClose className="h-[18px] w-[18px] shrink-0" /> Collapse</>}
          </button>
        </div>
      </aside>
      </TooltipProvider>

      <main className={`flex-1 min-w-0 ${collapsed ? "ml-16" : "ml-64"} min-h-screen flex flex-col transition-[margin] duration-200`}>
        <ImpersonationBanner impersonator={ctx.impersonator} targetName={user.name} />
        <header className="sticky top-0 z-30 h-16 border-b border-[#f0f0f3] bg-white/75 backdrop-blur-xl flex items-center justify-end gap-3 px-3 sm:px-6 md:px-10 lg:px-12">
          {user.account === "agency" && agencyToken() && (
            <button data-testid="back-to-agency" onClick={() => navigate("/console")} className="flex items-center gap-1.5 text-sm text-[#595D62] hover:text-[#06060C] mr-auto transition-colors"><ArrowLeft className="h-4 w-4" /> Mission Console</button>
          )}
          <NotificationBell slug={slug} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="topnav-user-menu" className="flex items-center gap-2.5 rounded-full border border-[#EEEFEF] bg-white pl-1.5 pr-3 py-1.5 hover:bg-[#F6F6FD] transition-colors">
                <Avatar name={user.name} size={28} account={user.account} userId={user.id} hasAvatar={user.has_avatar} version={user.avatar_version} />
                <span className="text-sm text-[#06060C] hidden sm:block">{user.name}</span><ChevronDown className="h-3.5 w-3.5 text-[#9FA1A4]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white border-[#EEEFEF]">
              <DropdownMenuLabel className="font-normal"><p className="text-sm font-medium text-[#06060C]">{user.name}</p>{user.job_title && <p className="text-xs text-[#595D62]">{user.job_title}</p>}<p className="text-xs text-[#9FA1A4]">{user.email}</p><span className="mt-1.5 inline-block font-mono-data text-[10px] uppercase tracking-wider text-[#595D62] bg-[#F6F6FD] border border-[#EEEFEF] rounded-full px-2 py-0.5">{WS_ROLE_LABEL[user.ws_role]}</span></DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#f0f0f3]" />
              <DropdownMenuItem data-testid="topnav-profile" onClick={() => setProfileOpen(true)} className="cursor-pointer focus:bg-[#F6F6FD]"><UserIcon className="h-4 w-4 mr-2" /> Profile</DropdownMenuItem>
              <DropdownMenuItem data-testid="topnav-welcome-video" onClick={() => setVideoOpen(true)} className="cursor-pointer focus:bg-[#F6F6FD]"><PlayCircle className="h-4 w-4 mr-2" /> Watch welcome video</DropdownMenuItem>
              <DropdownMenuItem data-testid="topnav-logout" onClick={ctx.logout} className="cursor-pointer focus:bg-[#F6F6FD]"><LogOut className="h-4 w-4 mr-2" /> Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <div className="px-3 sm:px-6 md:px-10 lg:px-12 py-6 sm:py-10 max-w-7xl flex-1 min-w-0"><Outlet /></div>
      </main>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} onSaved={ctx.reload} />
      <WorkspaceSwitcher open={switcherOpen} onOpenChange={setSwitcherOpen} slug={slug} canManageSettings={canManageSettings} onOpenSettings={() => { window.location.href = `/console/clients/${slug}`; }} />
      {!user.onboarding_completed && !ctx.impersonator && <Onboarding user={user} orgName={workspace.name} onDone={ctx.reload} showVideo />}
      {user.account === "agency" && agencyToken() && <CommsBubbles />}
      <WelcomeVideoDialog open={videoOpen || (user.onboarding_completed && !user.welcome_video_seen && user.account !== "agency" && !ctx.impersonator && !videoDismissed)} onClose={() => { setVideoOpen(false); if (!user.welcome_video_seen) { setVideoDismissed(true); api.post("/profile/welcome-video-seen").catch(() => {}); } }} />
    </div>
  );
}
